'use client';
/**
 * LE POINT DE RÈGLE — la procédure là où le choix se fait (lot REG-1).
 *
 * ── Le problème traité ──────────────────────────────────────────────────────
 *
 * Une procédure écrite ne se lit pas au moment où l'on décide. Elle se lit une
 * fois, à la formation, puis plus jamais — et le classeur continue d'exister,
 * d'être révisé et d'être présenté en audit sans gouverner un seul geste.
 * « L'outil remplace les tonnes de procédures » : la règle est donc portée LÀ
 * OÙ le choix se fait, derrière le « i ».
 *
 * ── La forme est fixe, et c'est le sujet ────────────────────────────────────
 *
 * Quatre lignes, toujours les mêmes, toujours dans cet ordre : LA RÈGLE ·
 * POURQUOI · LA NORME · DEPUIS. On apprend à les lire une fois, comme on
 * apprend à lire un paragraphe de norme. Un encart qui varierait de forme
 * redeviendrait une bulle d'aide qu'on parcourt en diagonale.
 *
 * ⚠️ NE PAS rendre l'une de ces lignes conditionnelle « pour gagner de la
 * place ». Une règle sans son POURQUOI se subit ; une règle sans son DEPUIS
 * n'est opposable à personne.
 *
 * ── Deux zones, deux propriétaires ──────────────────────────────────────────
 *
 * L'ÉNONCÉ (pourquoi, norme, depuis) appartient au RQ, qui l'approuve et le
 * versionne. Les VALEURS sont DÉRIVÉES du code qui les applique, côté moteur
 * (`backend/src/regles-nc.ts`), et arrivent marquées `lu`. L'écran les rend
 * visiblement distinctes : ce qui est lu du logiciel ne se discute pas, ce qui
 * est écrit par le RQ se discute.
 *
 * C'est aussi pourquoi le catalogue vient du SERVEUR et n'est pas recopié ici :
 * un miroir a toujours raison le jour où on l'écrit, et tort six mois plus tard.
 *
 * ── Au clic, pas au survol ──────────────────────────────────────────────────
 *
 * Une infobulle de survol convient à une phrase (`Help`, `RefISO` le font déjà).
 * Pas à quatre lignes qu'on veut relire, sélectionner, ou garder ouvertes en
 * remplissant le champ d'à côté : elle disparaît dès qu'on avance la souris
 * vers elle. Le survol dit donc ce qu'il y a derrière (`title`), et le clic
 * l'ouvre. Échap et le clic au-dehors referment.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { useGed, userName } from '@/lib/store';
import type { RegleServie } from '@/lib/store';
import { I, COUCHES } from '@/components/ui';

/** `2026-08-26` → `26/08/2026`. Une date ISO à l'écran est une date qu'on
 *  n'a pas fini de mettre en forme. */
const jour = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
};

const LIGNE: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: '.6px', color: 'var(--text-faint)',
  textTransform: 'uppercase', paddingTop: 2,
};

/** Une des quatre lignes. Le `<dt>`/`<dd>` n'est pas cosmétique : c'est une
 *  liste de définitions, et un lecteur d'écran l'annonce comme telle. */
function Ligne({ intitule, children }: { intitule: string; children: React.ReactNode }) {
  return (
    <>
      <dt style={LIGNE}>{intitule}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.55 }}>{children}</dd>
    </>
  );
}

/**
 * L'encart lui-même — extrait pour être rendu au portail ET testable seul.
 *
 * ⚠️ LA CONSIGNE D'ABORD, LA RÈGLE REPLIÉE (RQ, 08/10/2026 : « beaucoup trop
 * de texte, il faut juste que ça oriente sur quoi remplir »). Le déclarant lit
 * une ou deux phrases ; les quatre lignes documentaires (règle, pourquoi,
 * norme, depuis) restent à un clic, parce qu'elles sont le document approuvé
 * et que l'auditeur, lui, vient les lire. L'alarme « à revalider » reste en
 * tête quoi qu'il arrive : elle porte sur les valeurs, pas sur la consigne.
 */
export function EncartRegle({ r, detailOuvert = false }: { r: RegleServie; detailOuvert?: boolean }) {
  const settings = useGed((s) => s.settings);
  const [detail, setDetail] = React.useState(detailOuvert || !r.consigne);
  return (
    <div style={{
      width: 'min(460px, calc(100vw - 32px))',
      background: 'var(--surface-card)', border: '1px solid var(--border-brand)',
      borderRadius: 'var(--radius-md)', overflow: 'hidden',
      boxShadow: '0 6px 24px -10px rgba(14, 39, 64, .32)',
    }}>
      <header style={{
        background: 'var(--surface-brand-soft)', padding: '9px 14px',
        borderBottom: '1px solid var(--border-brand)',
        fontSize: 13.5, fontWeight: 700, color: 'var(--cyan-800, #105576)',
      }}>{r.titre}</header>

      {/* ⚠️ L'ALARME EST EN TÊTE, ET L'ENCART S'AFFICHE QUAND MÊME (REG-3).
          Le masquer priverait de la règle celui qui en a besoin, au motif
          qu'elle est peut-être un peu périmée — et une aide qui disparaît sans
          prévenir est pire qu'une aide datée. Le taire serait pire encore : on
          laisserait lire comme opposable un énoncé que le logiciel a cessé
          d'appliquer. */}
      {r.etat === 'a_revalider' && (
        <div role="status" style={{
          padding: '9px 14px', background: 'var(--amber-50, #FCF2E1)',
          borderBottom: '1px solid var(--amber-300, #EFC77B)',
          fontSize: 12.5, color: 'var(--amber-700, #8A5B00)', lineHeight: 1.5,
        }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <I n="alert" s={13} />Énoncé à revalider
          </strong>
          La règle a changé dans le logiciel depuis que cet énoncé a été approuvé.
          Les valeurs ci-dessous sont à jour ; l’explication qui les accompagne,
          peut-être pas.
        </div>
      )}

      {r.consigne && (
        <div style={{ padding: '12px 14px', display: 'grid', gap: 8 }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-strong)' }}>{r.consigne}</p>
          <button type="button" onClick={() => setDetail((v) => !v)} aria-expanded={detail}
            style={{
              justifySelf: 'start', background: 'none', border: 0, padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: 'var(--cyan-700, #0C6896)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
            <I n={detail ? 'chevron-right' : 'chevron-bas'} s={12} />
            {detail ? 'Replier la règle' : `La règle complète (${r.ref})`}
          </button>
        </div>
      )}

      {detail && <dl style={{
        margin: 0, padding: '13px 14px', display: 'grid', gridTemplateColumns: '82px 1fr', gap: '9px 13px',
        borderTop: r.consigne ? '1px solid var(--border-subtle)' : undefined,
      }}>
        <Ligne intitule="La règle">
          {r.regle.map((p, n) => <div key={n} style={{ marginBottom: n < r.regle.length - 1 ? 4 : 0 }}>{p}</div>)}
          {/* Les VALEURS, séparées de l'énoncé et marquées comme lues : le RQ
              doit voir d'un coup d'œil ce qu'il ne peut pas récrire. */}
          {!!r.valeurs.length && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 6 }}>
              {r.valeurs.map((v, n) => (
                <div key={n} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{
                    flex: 'none', background: 'var(--green-50, #E6F6EE)', border: '1px solid var(--green-200, #BFE6CC)',
                    borderRadius: 5, padding: '1px 6px', fontSize: 11.5, fontWeight: 700,
                    color: 'var(--green-700, #10603F)', whiteSpace: 'nowrap',
                  }}>lu du logiciel</span>
                  <span style={{ fontSize: 12.5 }}>{v.texte}</span>
                </div>
              ))}
            </div>
          )}
        </Ligne>

        <Ligne intitule="Pourquoi">{r.pourquoi}</Ligne>

        <Ligne intitule="La norme">
          {/* ⚠️ On CITE l'article, on ne recopie jamais le texte de la norme —
              et l'absence d'article est une réponse, pas un trou : elle dit que
              la règle est un choix du laboratoire, ce qui est une information. */}
          {r.norme.article
            ? <span style={{ color: 'var(--violet-600, #6D46C8)', fontWeight: 600 }}>{r.norme.article}</span>
            : <span style={{ color: 'var(--text-muted)' }}>Aucun article ne l’impose.</span>}
          {r.norme.nImposePas && (
            <span style={{ display: 'block', marginTop: 3, color: 'var(--text-muted)', fontWeight: 400 }}>
              {r.norme.nImposePas}
            </span>
          )}
        </Ligne>

        <Ligne intitule="Depuis">
          <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
            Approuvée par <b style={{ color: 'var(--text-body)' }}>{userName(settings, r.depuis.par)}</b> le {jour(r.depuis.le)}
            {r.depuis.applicableLe ? ` — applicable depuis le ${jour(r.depuis.applicableLe)}` : ''}
          </span>
        </Ligne>
      </dl>}

      <footer style={{
        borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)',
        padding: '7px 14px', fontSize: 11.5, color: 'var(--text-muted)',
      }}>
        <span style={{
          fontWeight: 800, color: 'var(--text-body)', background: 'var(--border-subtle)',
          borderRadius: 4, padding: '1px 7px', letterSpacing: '.3px',
        }}>{r.ref} · v{r.version}</span>
      </footer>
    </div>
  );
}

/**
 * Le « i » et ce qu'il ouvre.
 *
 * ⚠️ NE REND RIEN si l'ancre n'est servie par aucune règle. Les écrans posent
 * leurs « i » au fil de l'eau et le catalogue se remplit à son rythme (REG-2) :
 * un marqueur qui ouvrirait un encart vide serait pire que pas de marqueur.
 */
export function PointDeRegle({ ancre }: { ancre: string }) {
  const { regles } = useGed();
  const [ouvert, setOuvert] = React.useState<{ x: number; y: number } | null>(null);
  const bouton = React.useRef<HTMLButtonElement | null>(null);
  const r = (regles || []).find((x) => x.ancre === ancre);

  React.useEffect(() => {
    if (!ouvert) return undefined;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOuvert(null);
      // Le focus revient d'où il vient : sans cela, Échap laisse le clavier au
      // début du document et l'on a perdu sa place dans le formulaire.
      bouton.current?.focus();
    };
    const auDehors = () => setOuvert(null);
    window.addEventListener('keydown', auClavier);
    // `capture` : un clic sur un bouton d'un autre encart doit refermer
    // celui-ci AVANT que l'autre s'ouvre, sinon les deux cohabitent.
    window.addEventListener('mousedown', auDehors, true);
    return () => {
      window.removeEventListener('keydown', auClavier);
      window.removeEventListener('mousedown', auDehors, true);
    };
  }, [ouvert]);

  if (!r) return null;

  const basculer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (ouvert) { setOuvert(null); return; }
    const c = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setOuvert({ x: c.left, y: c.bottom + 6 });
  };

  return (
    <>
      <button
        ref={bouton} type="button" onClick={basculer}
        onMouseDown={(e) => e.stopPropagation()}
        // Le survol dit CE QU'IL Y A DERRIÈRE, le clic l'ouvre. Un « i » qui ne
        // promet rien ne se clique pas.
        title={r.titre}
        aria-expanded={!!ouvert}
        aria-label={`La règle : ${r.titre}`}
        style={{
          display: 'inline-grid', placeItems: 'center',
          // 24 px de zone tactile (WCAG 2.2), pour un glyphe de 15.
          width: 24, height: 24, marginLeft: 3, padding: 0,
          background: 'none', border: 0, cursor: 'pointer', verticalAlign: 'middle',
          color: ouvert ? 'var(--cyan-700, #0C6896)' : 'var(--text-muted)',
        }}>
        <I n="info" s={15} />
      </button>
      {ouvert && typeof document !== 'undefined' && createPortal(
        <div
          role="dialog" aria-label={r.titre}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            // Jamais hors de l'écran à droite : l'encart s'ouvre souvent depuis
            // un champ de la colonne de droite d'un formulaire en deux colonnes.
            left: Math.max(8, Math.min(ouvert.x, window.innerWidth - 476)),
            top: ouvert.y,
            zIndex: COUCHES.PANNEAU,
          }}>
          <EncartRegle r={r} />
        </div>,
        document.body,
      )}
    </>
  );
}
